import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  TableContainer,
  TableContent,
  TABLE_LAYOUT_OPTIONS,
  usePreventTableScrollingRef,
  Loader,
  TextButton,
  Card,
  Dialog,
} from "cdk-radial";
import classNames from "classnames";
import { capitalizeVisibility } from "../Utils/constants";
import PricingBreadcrumbs from "./PricingBreadcrumbs";
import { VIEW_BUNDLES, RETIRED_PRICING_PLANS, PRICING_LIST_PLANS } from "./screen";
import PricingPlanTile from "./PricingPlanTile";
import { useApiCall } from "./utils/apiUtils";
import config from "../config/app.conf.json";

const EllipsisCellRenderer = ({ value }) => (
  <div className="ellipsis_cell_renderer">{value || ""}</div>
);

function BundlePricePlanHistory(props) {
  const { auth, setScreen, clearPricePlanForHistory, ApiBundleManagementReducer } = props;
  const {
    selectedBundle,
    prevScreenForHistory,
    bundlePricingPlans: pricePlans,
    selectedPricePlanForHistory: planData,
  } = ApiBundleManagementReducer;
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPlanDetailsDialog, setShowPlanDetailsDialog] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const gridRef = useRef();
  const tableContentRef = usePreventTableScrollingRef();
  const { accessToken } = auth || {};

  // Use the shared API utility
  const makeApiCall = useApiCall(accessToken);

  const fetchPlanHistory = useCallback(async () => {
    setIsLoading(true);

    const bundleId = selectedBundle?.id;
    const planId = planData?.id;

    if (!bundleId || !planId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    try {
      const result = await makeApiCall(
        `${config.api.api_gateway_url}/v1/admin/bundles/${bundleId}/price-plans/${planId}/history`
      );

      if (result.success) {
        const historyData = await result.response.json();
        
        // Process the history data for the table
        const tableData = historyData.map((plan) => {
          let formattedDate = "N/A";
          let rawDate = null;
          if (plan?.updated) {
            const date = new Date(plan.updated);
            rawDate = date;
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            formattedDate = `${month}/${day}/${year}`;
          }
          
          return {
            plan,
            plan_type: capitalizeVisibility(plan?.planType) || "N/A",
            visibility: capitalizeVisibility(plan?.visibility) || "N/A",
            status: capitalizeVisibility(plan?.status) || "N/A",
            updated_on: formattedDate,
            rawUpdatedDate: rawDate,
          };
        });

        // Sort by updated date (most recent first)
        const sortedTableData = tableData.sort((a, b) => {
          if (!a.rawUpdatedDate && !b.rawUpdatedDate) return 0;
          if (!a.rawUpdatedDate) return 1;
          if (!b.rawUpdatedDate) return -1;
          return new Date(b.rawUpdatedDate) - new Date(a.rawUpdatedDate);
        });

        const finalTableData = sortedTableData.map(
          ({ rawUpdatedDate, ...rest }) => rest
        );

        setData(finalTableData);
      } else {
        console.error("Failed to fetch plan history:", result.error);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching plan history:", error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBundle?.id, planData?.id, makeApiCall]);

  useEffect(() => {
    fetchPlanHistory();
  }, [fetchPlanHistory]);

  const formatEffectiveDateRange = useCallback((plan) => {
    if (!plan?.created) return "N/A";
    
    const createdDate = new Date(plan.created);
    const day = String(createdDate.getDate()).padStart(2, "0");
    const month = String(createdDate.getMonth() + 1).padStart(2, "0");
    const year = createdDate.getFullYear();
    const formattedCreated = `${month}/${day}/${year}`;

    if (plan?.status && plan.status.toLowerCase() === 'active') {
      // If status is active: format(created) Till Present
      return `${formattedCreated} - Present`;
    } else if (plan?.status && plan.status.toLowerCase() === 'retired' && plan?.updated) {
      // If status is retired: format(created) Till format(updated)
      const updatedDate = new Date(plan.updated);
      const updatedDay = String(updatedDate.getDate()).padStart(2, "0");
      const updatedMonth = String(updatedDate.getMonth() + 1).padStart(2, "0");
      const updatedYear = updatedDate.getFullYear();
      const formattedUpdated = `${updatedMonth}/${updatedDay}/${updatedYear}`;
      return `${formattedCreated} - ${formattedUpdated}`;
    } else {
      // Fallback for other statuses
      return `${formattedCreated} - Present`;
    }
  }, []);

  const CustomNoRowsOverlay = () => (
    <div className="empty_no_rows_overlay">
      <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No pricing plan history available
      </div>
    </div>
  );

  const planDetailsRenderer = ({ data }) => {
    return (
      <TextButton
        text="View"
        onClick={() => {
          setSelectedPlanDetails(data);
          setShowPlanDetailsDialog(true);
        }}
        data-testid="view-details-button"
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "plan_type",
        headerName: "Plan Type",
        minWidth: 120,
        width: 120,
        sortable: true,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "visibility",
        headerName: "Visibility",
        minWidth: 100,
        width: 100,
        sortable: true,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "status",
        headerName: "Status",
        minWidth: 100,
        width: 100,
        sortable: true,
      },
      {
        cellRendererFramework: EllipsisCellRenderer,
        field: "updated_on",
        headerName: "Updated",
        minWidth: 120,
        width: 120,
        sortable: true,
      },
      {
        field: "plan_details",
        headerName: "Plan Details",
        cellClass: "plan-details-cell",
        cellRendererFramework: planDetailsRenderer,
        minWidth: 120,
        width: 120,
        sortable: false,
      },
    ],
    [planDetailsRenderer]
  );

  const handleGridReady = useCallback((gridParams) => {
    gridParams.api.sizeColumnsToFit();
    gridParams.api.refreshCells({ force: true });
  }, []);

  const breadcrumbs = useMemo(() => {
    if(prevScreenForHistory === PRICING_LIST_PLANS) {
      return [
        { label: "Bundle Management", onClick: () => setScreen(VIEW_BUNDLES) },
        { label: "Bundle Pricing", onClick: () => {
          clearPricePlanForHistory();
          setScreen(PRICING_LIST_PLANS);
        }},
        { label: "Plan History" },
      ];
    }
    return [
      { label: "Bundle Management", onClick: () => setScreen(VIEW_BUNDLES) },
      { label: "Bundle Pricing", onClick: () => setScreen(PRICING_LIST_PLANS)},
      { label: "Retired Plans", onClick: () => {
        clearPricePlanForHistory();
        setScreen(RETIRED_PRICING_PLANS);
      }},
      { label: "Plan History" },
    ];
  }, [setScreen, clearPricePlanForHistory, prevScreenForHistory]);

  if (isLoading) {
    return (
      <div className="pricing-plans_loader">
        <Loader
          className="pricing-plans-loader"
          data-testid="pricing_plans_loader"
          label="Loading plan history..."
          variant="inline"
        />
      </div>
    );
  }

  return (
    <>
    <div>
        <Card>
            <div className="breadcrumb-wrapper">
            <PricingBreadcrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div className="create-bundle-card">
            <div className="bundle-pricing-title">
                Bundle - {selectedBundle?.name}
            </div>
    
            <div>
                <div className="bundle-pricing-history-text">Plan History - {planData?.planName || planData?.plan_name || "Unknown Plan"}</div>
                <div />
            </div>
                <div>
                    <TableContainer
                    id="ag-grid-container"
                    className={classNames("ag-theme-balham")}
                    style={{ height: "550px" }}
                    >
                    <TableContent data-testid="table-content" ref={tableContentRef}>
                        <AgGridReact
                        columnDefs={columns}
                        rowData={data}
                        rowHeight={50}
                        style={{ width: "100%", height: "100%" }}
                        ref={gridRef}
                        onGridReady={handleGridReady}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        suppressScrollOnNewData={true}
                        getRowId={(params) => params.data.plan_name}
                        rowClass="grid-row"
                        frameworkComponents={{
                            customNoRowsOverlay: CustomNoRowsOverlay,
                        }}
                        noRowsOverlayComponent="customNoRowsOverlay"
                        />
                    </TableContent>
                    </TableContainer>
                </div>
            </div>
        </Card>
    </div>

      {/* Plan Details Dialog */}
      {selectedPlanDetails?.plan && <Dialog
        isOpen={showPlanDetailsDialog}
        onClose={() => {
          setShowPlanDetailsDialog(false);
          setSelectedPlanDetails(null);
        }}

        title={
            <div>
                <div>{`${selectedPlanDetails?.plan?.planName || "Plan Details"}`}</div>
                <div className="plan-dialog-date">
                  {`Effective ${formatEffectiveDateRange(selectedPlanDetails?.plan)}`}
                </div>
            </div>
        }
      >
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
          <PricingPlanTile
            planData={selectedPlanDetails}
            showDetailedInfo={true}
            customDescription={`${capitalizeVisibility(selectedPlanDetails?.plan?.visibility)} plan for unlimited Bundle use per dealership location.`}
            customQuotaText={`Unlimited API calls per month \nNo overage fees`}
          />
        </div>
      </Dialog>}
    </>
  );
}

export default BundlePricePlanHistory;